import { useEffect, useState, type ComponentType } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  ShieldCheck,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  useAgenticApprovalRun,
  type ApprovalRow,
} from "./useAgenticApprovalRun";

export type { ApprovalRow } from "./useAgenticApprovalRun";

// Sprint 10.x — Composant générique pour les cards "Outils IA" suivant le
// pattern : 1 clic → skill → liste d'approvals → Théo valide chaque.
// Spécialisé via un descriptor par card (cf wrappers PostMeetingRecapCard etc).

type AgentApprovalBatchCardProps<TSkillOutputApproval> = {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  iconClass?: string;
  ctaLabel: string;
  emptyLabel: string;
  skillId: string;
  skillInput: Record<string, unknown>;
  /** Map skill output approval → ApprovalRow basics. */
  toRow: (
    a: TSkillOutputApproval,
  ) => Pick<ApprovalRow, "approval_id" | "contact_label" | "subtitle">;
  /** Extract main editable text from agentic_pending_approvals.payload. */
  extractBody: (payload: Record<string, unknown>) => {
    body: string;
    subtitle?: string;
  };
  /** Apply user edits back into payload before save. */
  applyEdits: (
    payload: Record<string, unknown>,
    editedSubtitle: string,
    editedBody: string,
  ) => Record<string, unknown>;
  /** Optional success notif label per approval. */
  approveSuccessLabel?: string;
};

const pendingRow = (r: { id: string; summary: string }): ApprovalRow => ({
  approval_id: r.id,
  contact_label: r.summary,
  subtitle: "",
  state: "pending",
});

export function AgentApprovalBatchCard<TSkillOutputApproval>(
  props: AgentApprovalBatchCardProps<TSkillOutputApproval>,
) {
  const {
    title,
    description,
    icon: Icon,
    iconClass = "text-fabrik-violet",
    ctaLabel,
    emptyLabel,
    skillId,
    skillInput,
    toRow,
    extractBody,
    applyEdits,
    approveSuccessLabel = "Validé",
  } = props;

  const run = useAgenticApprovalRun({
    extractBody,
    applyEdits,
    approveSuccessLabel,
  });
  const {
    runState,
    setRunState,
    progress,
    approvals,
    setApprovals,
    runError,
    summaryMd,
    toggleBody,
    startEdit,
    cancelEdit,
    updateEdit,
    saveEdit,
    decide,
  } = run;
  const [restoredFromDb, setRestoredFromDb] = useState(false);

  // Auto-load des approvals encore pending issus d'un run précédent (survit
  // aux changements d'onglet et aux reloads). Filtre via le skill_id pour
  // ne pas confondre les approvals send_email de différents skills.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("agentic_pending_approvals")
        .select("id, summary, skill_runs!inner(skill_id)")
        .eq("skill_runs.skill_id", skillId)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(50);
      if (cancelled || error || !Array.isArray(data) || data.length === 0)
        return;
      setApprovals(
        (data as Array<{ id: string; summary: string }>).map(pendingRow),
      );
      setRunState("done");
      setRestoredFromDb(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [skillId, setApprovals, setRunState]);

  // ponytail: pas de useCallback, launch n'est qu'un onClick.
  const launch = () =>
    run.launch(
      skillId,
      skillInput,
      async (out, { runId, approvalsCreated }) => {
        const list: ApprovalRow[] = (
          (out.approvals as TSkillOutputApproval[] | undefined) ?? []
        ).map((a) => ({ ...toRow(a), state: "pending" as const }));
        // Fallback : si DeepSeek n'a pas restitué l'array approvals dans
        // son JSON final mais que request_approval a bien été appelé N fois,
        // on récupère les rows directement depuis agentic_pending_approvals
        // filtrés sur le run_id de cette exécution.
        if (list.length === 0 && approvalsCreated > 0 && runId != null) {
          const { data: rows } = await getSupabaseClient()
            .from("agentic_pending_approvals")
            .select("id, summary, kind, status")
            .eq("run_id", runId)
            .eq("status", "pending")
            .order("created_at", { ascending: true });
          if (Array.isArray(rows)) {
            return (rows as Array<{ id: string; summary: string }>).map(
              pendingRow,
            );
          }
        }
        return list;
      },
    );

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`size-4 ${iconClass}`} />
          <p className="text-[13px] font-semibold text-foreground">{title}</p>
          {approvals.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ({approvals.length})
            </span>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3">{description}</p>

        {runState === "idle" && (
          <Button size="sm" onClick={launch} className="w-full">
            {ctaLabel}
          </Button>
        )}
        {runState === "running" && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="size-3.5 animate-spin" />
            Analyse en cours… {progress > 0 ? `${progress} prêts` : ""}
          </div>
        )}
        {runState === "error" && (
          <div className="text-xs text-destructive py-2">
            Erreur : {runError}
            <Button size="sm" variant="ghost" className="ml-2" onClick={launch}>
              Réessayer
            </Button>
          </div>
        )}
        {runState === "done" && approvals.length === 0 && (
          <p className="text-xs text-muted-foreground py-1">
            {summaryMd ?? emptyLabel}
          </p>
        )}
        {restoredFromDb && approvals.some((a) => a.state === "pending") && (
          <Button
            size="sm"
            variant="outline"
            onClick={launch}
            className="w-full mt-2 text-xs"
          >
            Relancer une nouvelle analyse
          </Button>
        )}

        {approvals.length > 0 && (
          <div className="mt-4 border-t pt-3 space-y-2">
            <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 flex items-center gap-1">
              <ShieldCheck className="size-3" />
              {approvals.length} action(s) à valider
            </div>
            {approvals.map((a) => (
              <div
                key={a.approval_id}
                className="rounded border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 p-2"
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => toggleBody(a)}
                    className="flex-1 min-w-0 text-left flex items-start gap-1.5 hover:bg-amber-500/10 rounded px-1 -mx-1 -my-0.5 py-0.5"
                  >
                    {a.expanded ? (
                      <ChevronDown className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {a.contact_label}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.subtitle}
                      </div>
                    </div>
                  </button>
                  {a.state === "pending" && !a.editing && (
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => startEdit(a)}
                        title="Modifier"
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => decide(a, "reject")}
                        title="Refuser"
                      >
                        <X className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => decide(a, "execute")}
                        title="Valider"
                      >
                        <Check className="size-3.5" />
                      </Button>
                    </div>
                  )}
                  {(a.state === "applying" || a.state === "rejecting") && (
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                  )}
                  {a.state === "done" && (
                    <span className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 shrink-0">
                      <Check className="size-3" /> {approveSuccessLabel}
                    </span>
                  )}
                  {a.state === "rejected" && (
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      Refusé
                    </span>
                  )}
                  {a.state === "error" && (
                    <span
                      className="text-[11px] text-destructive shrink-0"
                      title={a.error}
                    >
                      Erreur
                    </span>
                  )}
                </div>

                {a.expanded && !a.editing && (
                  <div className="mt-2 pt-2 border-t border-amber-500/20 text-xs">
                    {a.bodyLoading ? (
                      <div className="flex items-center gap-1.5 text-muted-foreground py-1">
                        <Loader2 className="size-3 animate-spin" />
                        Chargement…
                      </div>
                    ) : a.bodyError ? (
                      <div className="text-destructive">{a.bodyError}</div>
                    ) : (
                      <pre className="whitespace-pre-wrap font-sans text-foreground leading-relaxed">
                        {a.body}
                      </pre>
                    )}
                  </div>
                )}

                {a.expanded && a.editing && (
                  <div className="mt-2 pt-2 border-t border-amber-500/20 space-y-2">
                    <input
                      type="text"
                      value={a.editedSubtitle ?? ""}
                      onChange={(e) =>
                        updateEdit(a.approval_id, "subtitle", e.target.value)
                      }
                      placeholder="Sujet / titre"
                      className="w-full text-xs px-2 py-1 rounded border bg-background"
                    />
                    <textarea
                      value={a.editedBody ?? ""}
                      onChange={(e) =>
                        updateEdit(a.approval_id, "body", e.target.value)
                      }
                      rows={8}
                      className="w-full text-xs px-2 py-1.5 rounded border bg-background font-sans leading-relaxed resize-y"
                    />
                    {a.saveError && (
                      <div className="text-[11px] text-destructive">
                        {a.saveError}
                      </div>
                    )}
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => cancelEdit(a)}
                      >
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => saveEdit(a)}
                        disabled={a.saving}
                      >
                        {a.saving ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          "Enregistrer"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
